import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, map } from 'rxjs';
import { ApiRateLimiterService } from '../services/api-rate-limiter.service';
import { ToastService } from '../services/toast.service';

/**
 * Security interceptor that provides:
 * 1. Client-side API rate limiting (prevents spam clicks on POST/PUT/DELETE)
 * 2. Request timestamp for replay attack detection
 * 3. XMLHttpRequest header for CORS preflight identification
 * 4. Response content-type validation
 * 5. Sensitive data detection in URLs (prevents accidental token leakage)
 */
export const securityInterceptor: HttpInterceptorFn = (req, next) => {
  const rateLimiter = inject(ApiRateLimiterService);
  const toastService = inject(ToastService);
  const isApiRequest = req.url.includes('/api/');

  if (!isApiRequest) {
    return next(req);
  }

  // Rate limiting — only apply to mutating requests (POST, PUT, DELETE, PATCH)
  // GET requests (loading images, lists, data) should never be rate limited
  const isMutatingRequest = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method.toUpperCase());
  if (isMutatingRequest && !rateLimiter.isAllowed(req.url)) {
    const retryAfter = rateLimiter.getRetryAfterSeconds(req.url);
    toastService.error(`Too many requests. Please wait ${retryAfter} seconds.`);
    return EMPTY;
  }

  // Check for sensitive data in URL (should be in body, not query params)
  const urlLower = req.url.toLowerCase();
  if (urlLower.includes('password=') || urlLower.includes('token=') || urlLower.includes('secret=')) {
    console.error('[Security] Sensitive data detected in URL. Request blocked:', req.url);
    return EMPTY;
  }

  // Add security headers
  const securedReq = req.clone({
    setHeaders: {
      'X-Request-Timestamp': Date.now().toString(),
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  return next(securedReq).pipe(
    map(event => {
      if (event instanceof HttpResponse) {
        const contentType = event.headers.get('content-type') || '';
        if (event.body && !contentType.includes('application/json') && !contentType.includes('text/') && !contentType.includes('image/')) {
          console.warn('[Security] Unexpected content-type from API:', contentType, req.url);
        }
      }
      return event;
    })
  );
};
